import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthProvider'
import { apiGet } from '../api/client'

type CaseData = {
  id: string
  status: string
  created_at: string
  updated_at: string
  customer_id: string
  customer_first_name: string
  customer_last_name: string
  customer_email: string | null
}

const STATUS_LABELS: Record<string, string> = {
  enquiry: 'Enquiry received',
  application: 'Application in progress',
  submitted: 'Submitted to lender',
  offered: 'Mortgage offer received',
  completed: 'Completed',
}

export default function CaseStatus() {
  const { user, token, logout, isLoading } = useAuth()
  const navigate = useNavigate()
  const [caseData, setCaseData] = useState<CaseData | null>(null)
  const [fetchError, setFetchError] = useState<string | null>(null)

  // In MVP 4 the case ID will come from the OTP magic-link. For now it's
  // derived from a query param or the first case associated with the user.
  const caseId = new URLSearchParams(window.location.search).get('case_id') ?? ''

  useEffect(() => {
    if (!isLoading && !user) {
      navigate('/', { replace: true })
    }
  }, [isLoading, user, navigate])

  useEffect(() => {
    if (!token || !caseId) return
    apiGet<{ case: CaseData }>(`/api/v1/cases/${caseId}`, token)
      .then(({ case: c }) => setCaseData(c))
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : 'Unknown error'
        if (message === 'UNAUTHORIZED') navigate('/', { replace: true })
        else setFetchError(message)
      })
  }, [token, caseId, navigate])

  if (isLoading) return <p>Loading…</p>

  return (
    <div style={{ padding: '2rem', fontFamily: 'system-ui, sans-serif', maxWidth: '600px', margin: '0 auto' }}>
      <header style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2rem' }}>
        <h1 style={{ margin: 0 }}>Your Mortgage</h1>
        <button onClick={logout}>Sign out</button>
      </header>

      {!caseId && (
        <p style={{ color: 'orange' }}>
          No case ID provided. Add <code>?case_id=...</code> to the URL.
        </p>
      )}

      {fetchError && <p style={{ color: 'red' }}>Error: {fetchError}</p>}

      {caseData && (
        <section>
          <p>
            Hello, <strong>{caseData.customer_first_name} {caseData.customer_last_name}</strong>
          </p>
          <div style={{ background: '#f5f5f5', borderRadius: '8px', padding: '1.5rem', marginTop: '1rem' }}>
            <p style={{ margin: '0 0 0.5rem', fontSize: '0.875rem', color: '#666' }}>Current status</p>
            <p style={{ margin: 0, fontSize: '1.25rem', fontWeight: 'bold' }}>
              {STATUS_LABELS[caseData.status] ?? caseData.status}
            </p>
            <p style={{ margin: '1rem 0 0', fontSize: '0.75rem', color: '#999' }}>
              Last updated: {new Date(caseData.updated_at).toLocaleDateString('en-GB', { dateStyle: 'long' })}
            </p>
          </div>
        </section>
      )}
    </div>
  )
}
